import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError, formatPhone } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import Timeline from "@/components/Timeline";
import BookingDrawer from "@/components/BookingDrawer";
import LabTrends from "@/components/LabTrends";
import WeightChart from "@/components/WeightChart";
import AppointmentActions from "@/components/AppointmentActions";
import EmptyState from "@/components/EmptyState";
import { CardSkeleton } from "@/components/Skeleton";
import { useClinic } from "@/context/ClinicContext";
import {
  ArrowLeft, Pill, FlaskConical, Plus, X, Loader2, Calendar, CalendarDays, Syringe, History, Clock, Heart, Sparkles, ArrowRight, Check, Lightbulb, ShieldCheck, UserX, Video,
} from "lucide-react";

const canEditProtocol = (role) => ["owner", "provider"].includes(role);
const canOrderLab = (role) => ["owner", "provider", "nurse"].includes(role);

function Modal({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid={testid}>
      <div className="bg-card rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-card">
          <h3 className="font-heading text-lg font-semibold">{title}</h3>
          <button onClick={onClose} data-testid="modal-close" className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function ProtocolModal({ patientId, onClose, onSaved }) {
  const [form, setForm] = useState({
    medication: "", dose: "", frequency: "", route: "",
    instructions: "", effective_date: new Date().toISOString().slice(0, 10), reason: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    try {
      await api.post(`/patients/${patientId}/treatment-plans`, form);
      onSaved();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
      setSaving(false);
    }
  };

  return (
    <Modal title="Update Treatment Plan" onClose={onClose} testid="protocol-modal">
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <L label="Treatment / Service"><input data-testid="protocol-medication" required value={form.medication} onChange={set("medication")} className="input" placeholder="e.g. Testosterone Cypionate, IV Hydration, Semaglutide" /></L>
          <L label="Dosage / Details"><input data-testid="protocol-dose" value={form.dose} onChange={set("dose")} className="input" placeholder="e.g. 100mg (optional)" /></L>
          <L label="Frequency"><input data-testid="protocol-frequency" value={form.frequency} onChange={set("frequency")} className="input" placeholder="e.g. Twice weekly (optional)" /></L>
          <L label="Route / Method">
            <select data-testid="protocol-route" value={form.route} onChange={set("route")} className="input">
              <option value="">Not applicable</option><option>Intramuscular</option><option>Subcutaneous</option><option>Intravenous</option><option>Oral</option><option>Topical</option>
            </select>
          </L>
          <L label="Effective date"><input data-testid="protocol-date" type="date" required value={form.effective_date} onChange={set("effective_date")} className="input" /></L>
        </div>
        <L label="Instructions"><textarea data-testid="protocol-instructions" value={form.instructions} onChange={set("instructions")} rows={2} className="input" /></L>
        <L label="Reason for change (required)"><textarea data-testid="protocol-reason" required value={form.reason} onChange={set("reason")} rows={2} className="input" placeholder="Clinical rationale — logged for audit" /></L>
        {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-md border border-border text-sm font-medium hover:bg-secondary">Cancel</button>
          <button data-testid="save-protocol-button" type="submit" disabled={saving} className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 flex items-center gap-2 disabled:opacity-60">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save treatment plan
          </button>
        </div>
      </form>
    </Modal>
  );
}

function LabOrderModal({ patientId, onClose, onSaved }) {
  const [form, setForm] = useState({ panel_name: "", due_date: new Date().toISOString().slice(0, 10), notes: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    try {
      await api.post(`/patients/${patientId}/labs`, form);
      onSaved();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
      setSaving(false);
    }
  };

  return (
    <Modal title="Order Lab" onClose={onClose} testid="lab-order-modal">
      <form onSubmit={submit} className="space-y-4">
        <L label="Panel name"><input data-testid="lab-panel-name" required value={form.panel_name} onChange={set("panel_name")} className="input" placeholder="e.g. Total Testosterone Panel" /></L>
        <L label="Due date"><input data-testid="lab-due-date" type="date" required value={form.due_date} onChange={set("due_date")} className="input" /></L>
        <L label="Notes"><textarea data-testid="lab-notes" value={form.notes} onChange={set("notes")} rows={2} className="input" /></L>
        {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-md border border-border text-sm font-medium hover:bg-secondary">Cancel</button>
          <button data-testid="save-lab-button" type="submit" disabled={saving} className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 flex items-center gap-2 disabled:opacity-60">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Order lab
          </button>
        </div>
      </form>
    </Modal>
  );
}

function L({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.05em] font-medium text-muted-foreground">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

/** Upcoming + history for one patient, plus booking on their behalf.
 *
 *  Front desk lives on the phone: the patient asks "when am I next in?" and the answer
 *  has to be on the profile they already have open, not a date range on /schedule they
 *  have to go and guess.
 */
function AppointmentsPanel({ patientId, patientName }) {
  const { clinic } = useClinic();
  const tz = clinic?.timezone || "America/New_York";
  const [data, setData] = useState(null);
  const [drawer, setDrawer] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    api.get(`/patients/${patientId}/appointments`)
      .then((r) => setData(r.data))
      .catch((e) => { setData({ upcoming: [], past: [] }); setError(formatApiError(e.response?.data?.detail)); });
  }, [patientId]);
  useEffect(() => { load(); }, [load]);

  const fmt = (startUtc) => new Date(startUtc).toLocaleString(undefined, {
    weekday: "short", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit", timeZone: tz,
  });

  // A move is not a cancellation — the same distinction the calendar and the portal make.
  const label = (a) => {
    if (a.status === "cancelled" && a.cancellation_kind === "rescheduled")
      return { text: "Moved", tone: "text-muted-foreground", strike: false };
    if (a.status === "cancelled") return { text: "Cancelled", tone: "text-muted-foreground", strike: true };
    if (a.status === "no_show") return { text: "No-show", tone: "text-amber-700", strike: false, icon: UserX };
    if (a.status === "completed") return { text: "Completed", tone: "text-emerald-700", strike: false, icon: Check };
    if (a.status === "requested") return { text: "Awaiting confirmation", tone: "text-amber-700", strike: false, icon: Clock };
    return { text: "Scheduled", tone: "text-muted-foreground", strike: false };
  };

  return (
    <div className="space-y-4" data-testid="patient-appointments-panel">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-lg font-semibold">Appointments</h3>
        <button data-testid="book-on-behalf-button" onClick={() => setDrawer(true)}
                className="inline-flex items-center gap-1.5 text-sm font-semibold bg-primary text-primary-foreground rounded-md px-3 py-2 hover:bg-primary/90">
          <Plus className="w-4 h-4" /> Book a visit
        </button>
      </div>

      {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</p>}

      {data === null ? <CardSkeleton lines={3} /> : (
        <>
          <div>
            <p className="text-sm font-medium text-muted-foreground mb-2">Upcoming</p>
            {data.upcoming.length === 0 ? (
              <EmptyState icon={CalendarDays} title="Nothing booked"
                          description={`${patientName} has no upcoming visits.`}
                          testid="no-upcoming-appointments" />
            ) : (
              <div className="space-y-2" data-testid="patient-upcoming-appointments">
                {data.upcoming.map((a) => {
                  const l = label(a);
                  return (
                    <div key={a.id} data-testid={`patient-appt-${a.id}`}
                         className="bg-card border border-border rounded-lg px-4 py-3">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div>
                          <p className="text-sm font-medium">{a.type}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {fmt(a.start_utc)}{a.provider ? ` · ${a.provider}` : ""}
                          </p>
                          {a.telehealth?.join_url && (
                            <span className="text-[10px] text-muted-foreground inline-flex items-center gap-1 mt-1">
                              <Video className="w-3 h-3" /> Telehealth
                            </span>
                          )}
                        </div>
                        <span className={`text-xs inline-flex items-center gap-1 ${l.tone}`}
                              data-testid={`patient-appt-status-${a.id}`}>
                          {l.icon && <l.icon className="w-3 h-3" />}{l.text}
                        </span>
                      </div>
                      <AppointmentActions appt={a} onChanged={load} className="mt-2.5" />
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div>
            <p className="text-sm font-medium text-muted-foreground mb-2">History</p>
            {data.past.length === 0 ? (
              <p className="text-sm text-muted-foreground">No past visits yet.</p>
            ) : (
              <div className="space-y-2" data-testid="patient-past-appointments">
                {data.past.map((a) => {
                  const l = label(a);
                  return (
                    <div key={a.id} data-testid={`patient-past-appt-${a.id}`}
                         className="bg-card border border-border rounded-lg px-4 py-2.5 flex flex-wrap items-center justify-between gap-2">
                      <div className={l.strike ? "line-through text-muted-foreground" : ""}>
                        <span className="text-sm font-medium">{a.type}</span>
                        <span className="text-sm text-muted-foreground"> · {fmt(a.start_utc)}</span>
                      </div>
                      <span className={`text-xs inline-flex items-center gap-1 ${l.tone}`}
                            data-testid={`patient-past-status-${a.id}`}>
                        {l.icon && <l.icon className="w-3 h-3" />}{l.text}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      <BookingDrawer open={drawer} onClose={() => setDrawer(false)} onBooked={load}
                     patient={{ id: patientId, name: patientName }} />
    </div>
  );
}

function ProgressPanel({ patientId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/patients/${patientId}/progress`)
      .then((r) => setData(r.data)).catch(() => setData({ entries: [] }));
  }, [patientId]);

  if (!data) return <CardSkeleton lines={3} />;
  const entries = data.entries || [];
  const latest = entries[entries.length - 1];

  return (
    <div className="bg-card border border-border rounded-lg p-5" data-testid="provider-weight-panel">
      <h3 className="font-heading text-base font-semibold">Weight the patient logged</h3>
      {entries.length === 0 ? (
        <p className="text-sm text-muted-foreground mt-2">Nothing logged yet.</p>
      ) : (
        <>
          <p className="font-heading text-2xl font-bold tabular-nums mt-1">
            {latest.value} <span className="text-sm font-medium text-muted-foreground">{latest.unit}</span>
            <span className="text-xs font-normal text-muted-foreground ml-2">
              last logged {latest.recorded_on}
            </span>
          </p>
          {/* Context, not a verdict: no target line, no colour that means good or bad. */}
          <WeightChart entries={entries} unit={latest.unit} height={180} />
        </>
      )}
    </div>
  );
}

function PrivacyPanel({ patientId, restrictions, labels, role }) {
  const canEdit = ["owner", "provider"].includes(role);
  const canDisclose = ["owner", "provider", "front_desk"].includes(role);
  const [flags, setFlags] = useState(restrictions || {});
  const [auths, setAuths] = useState([]);
  const [discs, setDiscs] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [authForm, setAuthForm] = useState({ recipient: "", scope: "", expiration: "" });
  const [discForm, setDiscForm] = useState({ recipient: "", recipient_type: "third_party", purpose: "", authority: "authorization" });
  const [err, setErr] = useState("");

  const reload = useCallback(() => {
    api.get(`/patients/${patientId}/authorizations`).then((r) => setAuths(r.data)).catch(() => {});
    api.get(`/patients/${patientId}/disclosures`).then((r) => setDiscs(r.data)).catch(() => {});
    api.get(`/patients/${patientId}/access-requests`).then((r) => setReqs(r.data)).catch(() => {});
  }, [patientId]);
  useEffect(() => { reload(); }, [reload]);

  const toggle = async (flag) => {
    const active = !flags[flag];
    try {
      const { data } = await api.put(`/patients/${patientId}/restrictions`, { flag, active });
      setFlags(data.restrictions);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const addAuth = async () => {
    setErr("");
    try {
      await api.post(`/patients/${patientId}/authorizations`, authForm);
      setAuthForm({ recipient: "", scope: "", expiration: "" }); reload();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const revokeAuth = async (aid) => { await api.post(`/authorizations/${aid}/revoke`).catch(() => {}); reload(); };

  const addDisc = async () => {
    setErr("");
    try {
      await api.post(`/patients/${patientId}/disclosures`, discForm);
      setDiscForm({ recipient: "", recipient_type: "third_party", purpose: "", authority: "authorization" }); reload();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="space-y-6" data-testid="privacy-tab">
      {err && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="privacy-error">{err}</p>}

      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="font-heading text-lg font-semibold mb-1">Privacy Restrictions</h3>
        <p className="text-sm text-muted-foreground mb-4">Patient-requested restrictions that the system enforces (45 CFR 164.522).</p>
        <div className="space-y-2" data-testid="restriction-list">
          {Object.entries(labels || {}).map(([flag, label]) => (
            <label key={flag} className="flex items-center gap-3 text-sm cursor-pointer">
              <input
                type="checkbox"
                data-testid={`restriction-${flag}`}
                disabled={!canEdit}
                checked={!!flags[flag]}
                onChange={() => toggle(flag)}
                className="w-4 h-4 rounded border-input"
              />
              <span>{label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="font-heading text-lg font-semibold mb-1">Authorizations to Disclose</h3>
        <p className="text-sm text-muted-foreground mb-4">Patient authorizations for releasing records (45 CFR 164.508).</p>
        {canEdit && (
          <div className="flex flex-wrap gap-2 mb-4">
            <input data-testid="auth-recipient" value={authForm.recipient} onChange={(e) => setAuthForm({ ...authForm, recipient: e.target.value })} placeholder="Recipient" className="rounded-md border border-input bg-card px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring" />
            <input data-testid="auth-scope" value={authForm.scope} onChange={(e) => setAuthForm({ ...authForm, scope: e.target.value })} placeholder="Scope (e.g. lab results)" className="rounded-md border border-input bg-card px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring" />
            <input data-testid="auth-expiration" type="date" value={authForm.expiration} onChange={(e) => setAuthForm({ ...authForm, expiration: e.target.value })} className="rounded-md border border-input bg-card px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring" />
            <button onClick={addAuth} disabled={!authForm.recipient || !authForm.scope} data-testid="auth-add-button" className="bg-primary text-primary-foreground rounded-md px-3 py-2 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60">Add</button>
          </div>
        )}
        <div className="divide-y divide-border" data-testid="auth-list">
          {auths.length === 0 ? <p className="text-sm text-muted-foreground">No authorizations on file.</p> : auths.map((a) => (
            <div key={a.id} className="flex items-center justify-between py-2.5 gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{a.recipient} · <span className="text-muted-foreground">{a.scope}</span></p>
                <p className="text-xs text-muted-foreground">{a.expiration ? `Expires ${a.expiration}` : "No expiration"}{a.revoked ? " · Revoked" : ""}</p>
              </div>
              {canEdit && !a.revoked && <button onClick={() => revokeAuth(a.id)} data-testid={`auth-revoke-${a.id}`} className="shrink-0 text-xs font-medium border border-border rounded-md px-2.5 py-1 hover:bg-secondary">Revoke</button>}
              {a.revoked && <span className="shrink-0 text-xs text-muted-foreground">Revoked</span>}
            </div>
          ))}
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="font-heading text-lg font-semibold mb-1">Accounting of Disclosures</h3>
        <p className="text-sm text-muted-foreground mb-4">Record of disclosures of this patient's PHI (45 CFR 164.528).</p>
        {canDisclose && (
          <div className="flex flex-wrap gap-2 mb-4">
            <input data-testid="disc-recipient" value={discForm.recipient} onChange={(e) => setDiscForm({ ...discForm, recipient: e.target.value })} placeholder="Recipient" className="rounded-md border border-input bg-card px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring" />
            <select data-testid="disc-type" value={discForm.recipient_type} onChange={(e) => setDiscForm({ ...discForm, recipient_type: e.target.value })} className="rounded-md border border-input bg-card px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring">
              <option value="third_party">Third party</option>
              <option value="provider">Provider</option>
              <option value="health_plan">Health plan</option>
            </select>
            <input data-testid="disc-purpose" value={discForm.purpose} onChange={(e) => setDiscForm({ ...discForm, purpose: e.target.value })} placeholder="Purpose" className="rounded-md border border-input bg-card px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring" />
            <button onClick={addDisc} disabled={!discForm.recipient || !discForm.purpose} data-testid="disc-add-button" className="bg-primary text-primary-foreground rounded-md px-3 py-2 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60">Record</button>
          </div>
        )}
        <div className="divide-y divide-border" data-testid="disclosure-list">
          {discs.length === 0 ? <p className="text-sm text-muted-foreground">No disclosures recorded.</p> : discs.map((d) => (
            <div key={d.id} className="py-2.5">
              <p className="text-sm font-medium">{d.recipient} <span className="text-xs text-muted-foreground">({d.recipient_type})</span></p>
              <p className="text-xs text-muted-foreground">{d.purpose} · {d.authority} · {new Date(d.date).toLocaleDateString()}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="font-heading text-lg font-semibold mb-1">Right-of-Access Requests</h3>
        <p className="text-sm text-muted-foreground mb-4">Patient downloads/requests of their record set (45 CFR 164.524).</p>
        <div className="divide-y divide-border" data-testid="access-request-list">
          {reqs.length === 0 ? <p className="text-sm text-muted-foreground">No access requests yet.</p> : reqs.map((r) => (
            <div key={r.id} className="flex items-center justify-between py-2.5">
              <p className="text-sm">{new Date(r.requested_at).toLocaleString()} · {r.format?.toUpperCase()}</p>
              <span className="text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-0.5">{r.status}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


export default function PatientProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("protocol");
  const [protoModal, setProtoModal] = useState(false);
  const [labModal, setLabModal] = useState(false);
  const [timeline, setTimeline] = useState(null);
  const [actionBusy, setActionBusy] = useState("");
  const [actionMsg, setActionMsg] = useState("");

  const load = useCallback(async () => {
    const res = await api.get(`/patients/${id}`);
    setData(res.data);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const doQuickAction = async (type) => {
    setActionBusy(type); setActionMsg("");
    try {
      const { data } = await api.post(`/patients/${id}/quick-action`, { concern_type: type });
      setActionMsg(data.message);
      await load();
    } catch (e) { /* ignore */ }
    setActionBusy("");
  };

  useEffect(() => {
    if (tab === "journey" && timeline === null) {
      api.get(`/patients/${id}/timeline`).then((r) => setTimeline(r.data.timeline)).catch(() => setTimeline([]));
    }
  }, [tab, id, timeline]);

  if (!data) {
    return (
      <div className="space-y-6" data-testid="patient-loading">
        <div className="h-4 w-32 bg-secondary rounded animate-pulse" />
        <div className="bg-card border border-border rounded-lg p-6 flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-secondary animate-pulse" />
          <div className="space-y-2">
            <div className="h-6 w-48 bg-secondary rounded animate-pulse" />
            <div className="h-3 w-64 bg-secondary rounded animate-pulse" />
          </div>
        </div>
        <CardSkeleton lines={4} />
      </div>
    );
  }

  const { patient, protocols, labs } = data;
  const health = data.relationship_health;
  const current = protocols.find((p) => p.status === "active");
  const history = protocols.filter((p) => p.status !== "active");
  const age = patient.dob ? Math.floor((Date.now() - new Date(patient.dob)) / 3.15576e10) : "—";
  const needsAttention = health?.status === "needs_attention";

  return (
    <div className="space-y-6" data-testid="patient-profile-page">
      <style>{`.input{width:100%;border:1px solid hsl(var(--input));background:hsl(var(--card));border-radius:0.375rem;padding:0.55rem 0.75rem;font-size:0.875rem;outline:none}.input:focus{box-shadow:0 0 0 2px hsl(var(--ring))}`}</style>

      <button data-testid="back-button" onClick={() => navigate("/patients")} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="w-4 h-4" /> Back to patients
      </button>

      {/* Header */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-heading text-xl font-bold">
              {patient.first_name[0]}{patient.last_name[0]}
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold tracking-tight">{patient.first_name} {patient.last_name}</h1>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground mt-1">
                <span>{patient.sex} · {age} yrs</span>
                <span>{patient.program}</span>
                <span>{patient.email}</span>
                {patient.phone && (
                  <a href={`tel:${patient.phone}`} data-testid="patient-phone"
                     className="hover:text-foreground">{formatPhone(patient.phone)}</a>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <StatusBadge status={patient.status} testid="patient-status-badge" />
            {health && (
              <div
                data-testid="relationship-status"
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${needsAttention ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}
              >
                {needsAttention ? <Sparkles className="w-3.5 h-3.5" /> : <Heart className="w-3.5 h-3.5" />}
                {health.summary}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Actionable attention card — insights, not labels */}
      {needsAttention && (
        <div className="bg-card border border-amber-200 rounded-lg overflow-hidden" data-testid="attention-card">
          <div className="px-5 py-3 bg-amber-50 border-b border-amber-200 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-600" />
            <h3 className="font-heading text-sm font-semibold text-amber-900">How we can help {patient.first_name} right now</h3>
          </div>
          {actionMsg && <p className="px-5 pt-3 text-sm text-emerald-700" data-testid="quick-action-result">{actionMsg}</p>}
          <ul className="divide-y divide-border">
            {health.concerns.map((c, i) => (
              <li key={i} className="px-5 py-4 flex items-start justify-between gap-4" data-testid={`concern-${i}`}>
                <div className="min-w-0">
                  <p className="font-medium text-sm">{c.label}</p>
                  <p className="text-sm text-muted-foreground mt-0.5">{c.why}</p>
                  <p className="text-sm font-medium text-foreground mt-1.5 inline-flex items-center gap-1.5">
                    <ArrowRight className="w-3.5 h-3.5 text-emerald-600" /> {c.action}
                  </p>
                </div>
                <button
                  data-testid={`quick-action-${c.type}`}
                  onClick={() => doQuickAction(c.type)}
                  disabled={actionBusy === c.type}
                  className="shrink-0 inline-flex items-center gap-1.5 bg-primary text-primary-foreground rounded-md px-3 py-2 text-xs font-semibold hover:bg-primary/90 disabled:opacity-60"
                >
                  {actionBusy === c.type ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                  Do this
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Check-in context — insight, not a conclusion */}
      {data.context_insights?.length > 0 && (
        <div className="space-y-2" data-testid="context-insights">
          {data.context_insights.map((ins, i) => (
            <div key={i} className={`flex items-start gap-3 rounded-lg border px-4 py-3 ${ins.tone === "positive" ? "bg-emerald-50/60 border-emerald-200" : "bg-sky-50/60 border-sky-200"}`} data-testid={`context-insight-${i}`}>
              <Lightbulb className={`w-4 h-4 mt-0.5 shrink-0 ${ins.tone === "positive" ? "text-emerald-600" : "text-sky-600"}`} />
              <p className="text-sm text-foreground">{ins.text}</p>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border">
        {[
          { k: "protocol", label: "Treatment Plan", icon: Pill },
          { k: "labs", label: `Labs (${labs.length})`, icon: FlaskConical },
          { k: "appointments", label: "Appointments", icon: CalendarDays },
          { k: "journey", label: "Journey", icon: Clock },
          { k: "privacy", label: "Privacy", icon: ShieldCheck },
        ].map((t) => (
          <button
            key={t.k}
            data-testid={`tab-${t.k}`}
            onClick={() => setTab(t.k)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.k ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "protocol" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="font-heading text-lg font-semibold">Current Treatment Plan</h3>
            {canEditProtocol(user.role) && (
              <button data-testid="update-protocol-button" onClick={() => setProtoModal(true)} className="inline-flex items-center gap-1.5 text-sm font-semibold bg-primary text-primary-foreground rounded-md px-3 py-2 hover:bg-primary/90">
                <Plus className="w-4 h-4" /> Update Plan
              </button>
            )}
          </div>

          {current ? (
            <div className="bg-card border border-border rounded-lg p-6" data-testid="current-protocol-card">
              <div className="flex items-center gap-2 mb-4">
                <Syringe className="w-5 h-5 text-primary" />
                <span className="font-heading text-xl font-bold">{current.medication}</span>
                <StatusBadge status="active" />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <Info label="Dosage" value={current.dose || "—"} />
                <Info label="Frequency" value={current.frequency || "—"} />
                <Info label="Route" value={current.route || "—"} />
                <Info label="Effective" value={current.effective_date?.slice(0, 10)} />
              </div>
              {current.instructions && <p className="text-sm text-muted-foreground mt-4 pt-4 border-t border-border"><span className="font-medium text-foreground">Instructions:</span> {current.instructions}</p>}
              <p className="text-sm text-muted-foreground mt-2"><span className="font-medium text-foreground">Reason:</span> {current.reason}</p>
              <p className="text-xs text-muted-foreground mt-3">Set by {current.created_by}</p>
            </div>
          ) : (
            <div className="bg-card border border-dashed border-border rounded-lg p-8 text-center text-sm text-muted-foreground">No active treatment plan. Add one to get started.</div>
          )}

          {history.length > 0 && (
            <div>
              <h4 className="flex items-center gap-2 font-heading text-base font-semibold mb-3"><History className="w-4 h-4" /> Plan History</h4>
              <div className="space-y-3" data-testid="protocol-history">
                {history.map((p) => (
                  <div key={p.id} className="bg-card border border-border rounded-lg p-4 flex items-start justify-between">
                    <div>
                      <p className="font-medium">{p.medication} — {p.dose}, {p.frequency}</p>
                      <p className="text-sm text-muted-foreground mt-0.5">{p.reason}</p>
                      <p className="text-xs text-muted-foreground mt-1">Effective {p.effective_date?.slice(0, 10)} · {p.created_by}</p>
                    </div>
                    <StatusBadge status="ended" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "labs" && (
        <div className="space-y-4">
          <ProgressPanel patientId={id} />
          <LabTrends path={`/patients/${id}/lab-trends`} />
          <div className="flex items-center justify-between">
            <h3 className="font-heading text-lg font-semibold">Lab Orders & Results</h3>
            {canOrderLab(user.role) && (
              <button data-testid="order-lab-button" onClick={() => setLabModal(true)} className="inline-flex items-center gap-1.5 text-sm font-semibold bg-primary text-primary-foreground rounded-md px-3 py-2 hover:bg-primary/90">
                <Plus className="w-4 h-4" /> Order Lab
              </button>
            )}
          </div>

          {labs.length === 0 ? (
            <div className="bg-card border border-dashed border-border rounded-lg p-8 text-center text-sm text-muted-foreground">No labs ordered yet.</div>
          ) : (
            <div className="space-y-3" data-testid="patient-labs-list">
              {labs.map((lab) => (
                <div key={lab.id} className="bg-card border border-border rounded-lg p-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FlaskConical className="w-4 h-4 text-muted-foreground" />
                      <span className="font-medium">{lab.panel_name}</span>
                    </div>
                    <StatusBadge status={lab.status} />
                  </div>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground mt-1.5">
                    <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> Due {lab.due_date?.slice(0, 10)}</span>
                    {lab.reviewed_by && <span>Reviewed by {lab.reviewed_by}</span>}
                  </div>
                  {lab.results?.length > 0 && (
                    <table className="w-full text-sm mt-4 border-t border-border pt-2">
                      <thead>
                        <tr className="text-xs text-muted-foreground">
                          <th className="text-left font-medium py-1.5">Marker</th>
                          <th className="text-right font-medium py-1.5">Value</th>
                          <th className="text-right font-medium py-1.5 hidden sm:table-cell">Reference</th>
                          <th className="text-right font-medium py-1.5">Flag</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lab.results.map((r, i) => (
                          <tr key={i} className="border-t border-border/60">
                            <td className="py-1.5">{r.marker}</td>
                            <td className="py-1.5 text-right tabular-nums font-medium">{r.value} {r.unit}</td>
                            <td className="py-1.5 text-right tabular-nums text-muted-foreground hidden sm:table-cell">{r.ref_range}</td>
                            <td className="py-1.5 text-right"><StatusBadge status={r.flag} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "appointments" && (
        <AppointmentsPanel patientId={id} patientName={`${patient.first_name} ${patient.last_name}`} />
      )}

      {tab === "journey" && (
        <div className="bg-card border border-border rounded-lg p-6" data-testid="journey-tab">
          <h3 className="font-heading text-lg font-semibold mb-4">Relationship Timeline</h3>
          {timeline === null ? <p className="text-sm text-muted-foreground">Loading timeline…</p> : <Timeline events={timeline} testid="staff-timeline-list" />}
        </div>
      )}

      {tab === "privacy" && (
        <PrivacyPanel patientId={id} restrictions={data.restrictions} labels={data.restriction_labels} role={user?.role} />
      )}

      {protoModal && <ProtocolModal patientId={id} onClose={() => setProtoModal(false)} onSaved={() => { setProtoModal(false); load(); }} />}
      {labModal && <LabOrderModal patientId={id} onClose={() => setLabModal(false)} onSaved={() => { setLabModal(false); load(); }} />}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-[0.05em] font-medium text-muted-foreground">{label}</p>
      <p className="font-medium mt-1">{value}</p>
    </div>
  );
}
